import {
  Backdrop,
  CircularProgress,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { D_INV22G, TOKEN, USER, USERNAME } from "constants/api/localStorage";
import { useAppDispatch } from "hooks/StoreHooks";
import { Login } from "models/Resources/Login";
import { UserAuthorizations } from "models/UserAuthorization";
import Connection from "pages/Connection/Connection";
import Loading from "pages/Loading";
import { useEffect, useState } from "react";
import { HashRouter } from "react-router";
import { routes } from "routes/routes";
import { connectionApi } from "services/api/connectionApi";
import { setd_inv22g, setToken, setUser } from "store/Slices/appStateSlice";

import AlertSnackbar from "components/NotificationZone/Snackbars/AlertSnackbar";
import { Severity } from "constants/Severity";
import ApplicatWS from "events/ApplicatWS";
import { NotificationEvent, NotificationReceivedEvent } from "events/Event";
import { NotificationResourceAction } from "models/Resources/NotificationResource";
import { SnackbarProvider } from "notistack";
import "./App.scss";
import "./constants/scss/fonts/DefaultFonts.scss";

export const appTheme = createTheme({
  typography: {
    fontFamily: '"Kern Standard", sans-serif',
  },
});

function App() {
  const [isOpen, setOpen] = useState(false);
  const [message, setMessage] = useState({} as { text: string; severity: Severity; duration?: number, actions?: NotificationResourceAction[] });

  const handleSnackbarClose = () => {
    setOpen(false);
  };
  const dispatch = useAppDispatch();

  const APPLICAT_TOKEN_PARAM = "applicatauthtoken";
  
  useEffect(() => {
    NotificationReceivedEvent.subscribe((e) => { 
      if (!e.archivedBy) {
        setMessage({ text: e.title ?? (e.text ?? e.source), severity: e.severity, duration: e.duration, actions: e.actions});
        setOpen(true);
      }
    });
    NotificationEvent.subscribe((e) => { 
      setMessage(e);
      setOpen(true);
    });
  }, []);

  const [logInWithToken] =
    connectionApi.endpoints.logInWithToken.useLazyQuery();
  const [urlLoaded, setUrlLoaded] = useState(false);

  useEffect(() => {
    const getUrlInfos = async () => {
      const search = new URLSearchParams(window.location.href.split("?")[1]);
      if (search.get(APPLICAT_TOKEN_PARAM) !== null) {
        let token = search.get(APPLICAT_TOKEN_PARAM);
        const loginInfos = (await logInWithToken(token!).unwrap()) as Login;
        if (loginInfos.success) {
          token = loginInfos.token;
          setLoginInfos(loginInfos);
        }
        window.history.replaceState(
          null,
          "",
          window.location.href.split("?")[0]
        );
      } else if (search.get(TOKEN) !== null && search.get(USER) !== null) {
        const token = search.get(TOKEN);
        localStorage.setItem(USER, `${search.get(USER)}`);
        localStorage.setItem(TOKEN, `${search.get(TOKEN)}`);
        dispatch(setToken(`Bearer ${token}`));
        dispatch(setUser(search.get(USER)!));
        window.history.replaceState(
          null,
          "",
          window.location.href.split("?")[0]
        );
      }
      setUrlLoaded(true);
    };
    getUrlInfos();
  }, [dispatch]);

  function setLoginInfos(loginInfos: Login) {
    setLoggedUser(loginInfos.user);
    setLoggedName(loginInfos.name);
    setLoggedToken(loginInfos.token);
    setWantedAuthorizations(loginInfos.authorizations);
  }

  function setLoggedUser(user: string) {
    localStorage.setItem(USER, user);
    dispatch(setUser(user));
  }

  function setLoggedName(name: string) {
    localStorage.setItem(USERNAME, name);
  }

  function setLoggedToken(token: string) {
    localStorage.setItem(TOKEN, token);
    dispatch(setToken(`${token}`));
  }

  function setWantedAuthorizations(authorizations: UserAuthorizations[]) {
    const d_inv22g = authorizations.find(
      (a: UserAuthorizations) => a.functName === D_INV22G
    );
    if (d_inv22g) {
      dispatch(setd_inv22g(d_inv22g));
    } else {
      localStorage.clear();
    }
  }

  return urlLoaded ? (
    <div style={ { height: "100%", width: "100%"}}>
    <Connection>
      <Loading>
        <ThemeProvider theme={appTheme}>
          <HashRouter>{routes}</HashRouter>
        </ThemeProvider>
      </Loading>
    </Connection>
    <div id="hidden-treasure">
            <SnackbarProvider
              maxSnack={6}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              style={{
                display: "flex",
                flexWrap: "nowrap",
                maxWidth: "50vw",
              }}>

          <AlertSnackbar
            isOpen={isOpen}
            message={message.text}
            autoHideDuration={message.duration ?? 5000}
            severity={message.severity}
            onClose={handleSnackbarClose}
            actions={message.actions}
          />
        </SnackbarProvider>
      <ApplicatWS />
      </div>
      </div>
  ) : (
    <Backdrop sx={{ color: "#fff", zIndex: 2147483647 }} open={true}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );
}

export default App;
